import { defineSlotRecipe } from "@pandacss/dev";

export const aromaBanner = defineSlotRecipe({
  slots: [
    "container",
    "logo",
    "content",
    "text",
    "removeTrigger",
    "buttons",
    "desktopLogo",
    "mobileLogo",
    "title",
    "description",
    "contextualIconWrap",
    "textWrap",
    "illustration",
  ],
  jsx: ["Banner"],
  className: "aroma-banner",
  base: {
    container: {
      top: "0",
      left: "0",
      right: "0",
      width: "100%",
      color: "{colors.white}",
      borderRadius: 0,
      padding: "padding.2",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexDirection: "row",
      flexGrow: 1,
      gap: "gap.2",
    },
    logo: {
      display: "none",
      position: "absolute",
      height: "xl",
      alignItems: "center",

      "&>img, &>svg": {
        maxHeight: "m",
        left: "padding.2",
      },
    },
    content: {
      alignItems: "center",
      margin: "auto",
      justifyContent: "space-between",
      width: "100%",
    },
    text: {
      alignItems: "center",
      gap: "gap.4",
    },
    removeTrigger: {
      alignSelf: "flex-start",
      width: "xl",
    },
    buttons: {
      gap: "gap.2",
      width: "auto",
      "& > button": {
        width: "auto",
        mdDown: {
          "& > span": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "150px",
          },
        },
      },
    },
    title: {
      textStyle: "title.s",
    },
    description: {
      textStyle: "paragraph.m.regular",
    },
    contextualIconWrap: {
      display: "none",
    },
    textWrap: {
      flexDirection: "column",
      alignItems: "start",
      xs: {
        flexDirection: "row",
        alignItems: "baseline",
      },
    },
  },
  variants: {
    colorTheme: {
      neutral: {
        container: {
          backgroundColor: "background.neutral.base.bold",
        },
      },
      info: {
        container: {
          backgroundColor: "background.functional.info.bold",
        },
      },
      success: {
        container: {
          backgroundColor: "background.functional.success.bold",
        },
      },
      warning: {
        container: {
          backgroundColor: "background.functional.warning.bold",
        },
      },
      error: {
        container: {
          backgroundColor: "background.functional.error.bold",
        },
      },
      blue: {
        container: {
          backgroundColor: "background.brand.bold",
        },
        title: {
          textStyle: "display.s",
        },
      },
      oat: {
        container: {
          backgroundColor: "background.brand.complementary",
          color: "content.neutral.base.bold",
        },
        title: {
          textStyle: "display.s",
        },
      },
    },
    form: {
      bar: {
        container: {
          overflow: "hidden",
        },
        content: {
          gap: "gap.2",
          position: "relative",
        },
        illustration: {
          display: "flex",
          alignItems: "center",

          "&>img, &>svg": {
            position: "absolute",
            height: "auto",
            maxWidth: "107px",
          },
        },
        buttons: {
          marginBlock: "auto",
        },
        text: {
          gap: "gap.2",
        },
        textWrap: {
          flexWrap: "wrap",
        },
        title: {
          marginRight: "gap.2",
        },
        removeTrigger: {
          marginBlock: "auto",
        },
      },
      square: {
        container: {
          maxWidth: "width.s",
          paddingY: "padding.6",
          paddingX: "padding.10",
        },
        content: {
          gap: "gap.4",
          flexDirection: "column",
          justifyContent: "space-between",
          position: "relative",
        },
        illustration: {
          "&>img, &>svg": {
            maxHeight: "120px",
            width: "fit-content",
          },
        },
        text: {
          flexDirection: "column",
        },
        buttons: {
          width: "100%",
          flexDirection: "column-reverse",
        },
        textWrap: {
          justifyContent: "center",
          flexDirection: "column",
          alignItems: "center",
        },
        removeTrigger: {
          position: "absolute",
          top: "padding.2",
          right: "padding.2",
        },
      },
      rectangle: {
        container: {
          margin: "inherit",
          maxWidth: "width.2xl",
          padding: "padding.6",
          position: "relative",
        },
        content: {
          gap: "gap.4",
          alignItems: "start",
          flexWrap: "nowrap",
        },
        illustration: {
          "&>img, &>svg": {
            maxHeight: "120px",
            width: "fit-content",
          },
        },
        textWrap: {
          flexDirection: "column",
        },
        buttons: {
          flexDirection: "row-reverse",
          justifyContent: "left",
        },
        removeTrigger: {
          position: "absolute",
          top: "padding.2",
          right: "padding.2",
        },
      },
    },
    spot: {
      fixed: {
        container: {
          position: "fixed",
        },
        content: {
          md: {
            maxWidth: "1120px",
          },
        },
      },
      float: {
        container: {
          position: "relative",
          borderRadius: "l",
        },
      },
    },
    isRemovable: {
      true: {},
      false: {},
    },
    showLogo: {
      true: {},
      false: {},
    },
  },
  compoundVariants: [
    {
      form: "bar",
      colorTheme: "oat",
      css: {
        description: {
          textStyle: "paragraph.l.regular",
        },
      },
    },
    {
      form: "bar",
      colorTheme: "blue",
      css: {
        description: {
          textStyle: "paragraph.l.regular",
        },
      },
    },
    {
      form: "bar",
      colorTheme: "oat",
      showLogo: true,
      css: {
        desktopLogo: {
          maxWidth: "107px",
          display: "none",
          xs: {
            display: "flex",
          },
        },
        mobileLogo: {
          maxWidth: "m",
          display: "block",
          xs: {
            display: "none",
          },
        },
      },
    },
    {
      form: "bar",
      colorTheme: "blue",
      showLogo: true,
      css: {
        desktopLogo: {
          maxWidth: "107px",
          display: "none",
          xs: {
            display: "flex",
          },
        },
        mobileLogo: {
          display: "block",
          xs: {
            display: "none",
          },
        },
      },
    },
    {
      form: "bar",
      spot: "float",
      css: {
        container: {
          width: "calc(100% - 32px)",
          xs: { width: "calc(100% - 48px)" },
          md: { maxWidth: "1120px" },
        },
      },
    },
    {
      form: "bar",
      isRemovable: true,
      spot: "fixed",
      css: {
        content: {
          lg: {
            marginRight: "auto",
          },
        },
      },
    },
    {
      form: "bar",
      showLogo: true,
      spot: "float",
      colorTheme: "blue",
      css: {
        content: {
          marginLeft: "margin.10",
          xs: {
            marginLeft: "118px",
          },
        },
      },
    },
    {
      form: "bar",
      showLogo: true,
      spot: "fixed",
      colorTheme: "blue",
      css: {
        content: {
          xs: {
            marginLeft: "118px",
          },
          md: {
            marginLeft: "auto",
          },
        },
      },
    },
    {
      form: "bar",
      showLogo: true,
      spot: "float",
      colorTheme: "oat",
      css: {
        content: {
          marginLeft: "margin.10",
          xs: {
            marginLeft: "118px",
          },
        },
      },
    },
    {
      form: "bar",
      showLogo: true,
      spot: "fixed",
      colorTheme: "oat",
      css: {
        content: {
          xs: {
            marginLeft: "118px",
          },
          md: {
            marginLeft: "auto",
          },
        },
      },
    },
    {
      form: "bar",
      colorTheme: "neutral",
      css: {
        contextualIconWrap: {
          display: "block",
        },
      },
    },
    {
      form: "bar",
      colorTheme: "info",
      css: {
        contextualIconWrap: {
          display: "block",
        },
      },
    },
    {
      form: "bar",
      colorTheme: "success",
      css: {
        contextualIconWrap: {
          display: "block",
        },
      },
    },
    {
      form: "bar",
      colorTheme: "warning",
      css: {
        contextualIconWrap: {
          display: "block",
        },
      },
    },
    {
      form: "bar",
      colorTheme: "error",
      css: {
        contextualIconWrap: {
          display: "block",
        },
      },
    },
  ],
  defaultVariants: {
    colorTheme: "neutral",
    spot: "float",
    form: "bar",
  },
});
